/**
 * LiveTerminalPanel — настоящий host-shell через WebSocket (P3 wave-1).
 *
 * Подключается к `/api/v1/devops/terminal?token=JWT`. На бэке pty.fork() →
 * `nsenter -t 1 -- /bin/bash -i`, поэтому это полноценный root-shell в
 * host-namespace.
 *
 * Без xterm.js — лёгкая реализация: ANSI-вывод парсим через ansi_up,
 * рендерим в <pre>. Ввод читается из `<input>`-строки и отправляется на
 * сервер. Локальное эхо мы НЕ делаем — bash сам отрисует через PTY.
 *
 * Поддержано: история команд (↑/↓), Ctrl-C (отправить \x03), очистка экрана,
 * resize при ресайзе window.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Terminal as TerminalIcon,
  Trash2,
  WifiOff,
  Wifi,
  CornerDownLeft,
} from "lucide-react";
import { AnsiUp } from "ansi_up";
import { buildDevopsTerminalUrl } from "../../lib/api";
import { cn } from "../../lib/cn";

const MAX_BUFFER = 200_000; // ~200KB видимого вывода

export function LiveTerminalPanel() {
  const [output, setOutput] = useState("");
  const [status, setStatus] = useState<"idle" | "connecting" | "open" | "closed" | "error">(
    "idle",
  );
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState<number>(-1);

  const wsRef = useRef<WebSocket | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ansi = useMemo(() => {
    const u = new AnsiUp();
    u.use_classes = false;
    return u;
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) {
      return;
    }
    setStatus("connecting");
    setErrMsg(null);
    try {
      const url = buildDevopsTerminalUrl();
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        setStatus("open");
        // Запросим базовый размер
        try {
          ws.send(JSON.stringify({ type: "resize", cols: 100, rows: 30 }));
        } catch {
          /* noop */
        }
      };
      ws.onmessage = (ev) => {
        const text = typeof ev.data === "string" ? ev.data : "";
        setOutput((prev) => {
          const next = prev + text;
          return next.length > MAX_BUFFER ? next.slice(next.length - MAX_BUFFER) : next;
        });
      };
      ws.onerror = () => {
        setStatus("error");
        setErrMsg("WebSocket error");
      };
      ws.onclose = (ev) => {
        setStatus("closed");
        if (ev.code === 4401) setErrMsg("Не авторизован (404 1). Перезайдите.");
        else if (ev.code === 4403) setErrMsg("Терминал отключён в настройках.");
        else if (!errMsg) setErrMsg(`Соединение закрыто (code ${ev.code})`);
      };
    } catch (e) {
      setStatus("error");
      setErrMsg(e instanceof Error ? e.message : String(e));
    }
  }, [errMsg]);

  useEffect(() => {
    connect();
    return () => {
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Автоскролл к низу
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [output]);

  // resize — отправляем pty
  useEffect(() => {
    function onResize() {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1) return;
      // Грубо считаем cols/rows по контейнеру: 8px на колонку, 16px на строку (моно)
      const el = scrollRef.current;
      if (!el) return;
      const cols = Math.max(40, Math.floor(el.clientWidth / 8));
      const rows = Math.max(10, Math.floor(el.clientHeight / 16));
      try {
        ws.send(JSON.stringify({ type: "resize", cols, rows }));
      } catch {
        /* noop */
      }
    }
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, [status]);

  function send(text: string) {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(text);
    } catch {
      /* noop */
    }
  }

  function submit() {
    if (!input && historyIdx === -1) {
      send("\n");
      return;
    }
    send(input + "\n");
    if (input.trim()) {
      setHistory((h) => [...h.slice(-99), input]);
    }
    setInput("");
    setHistoryIdx(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (history.length === 0) return;
      const newIdx = historyIdx === -1 ? history.length - 1 : Math.max(0, historyIdx - 1);
      setHistoryIdx(newIdx);
      setInput(history[newIdx] ?? "");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIdx === -1) return;
      const newIdx = historyIdx + 1;
      if (newIdx >= history.length) {
        setHistoryIdx(-1);
        setInput("");
      } else {
        setHistoryIdx(newIdx);
        setInput(history[newIdx] ?? "");
      }
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
      // Если выделен текст — не перехватываем (даём скопировать)
      const sel = window.getSelection();
      if (sel && sel.toString().length > 0) return;
      e.preventDefault();
      send("\x03");
      setInput("");
      setHistoryIdx(-1);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
      e.preventDefault();
      send("\x04");
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      send(input + "\t");
      // Не очищаем input — bash дорисует завершение в выводе
      return;
    }
  }

  const html = useMemo(() => ansi.ansi_to_html(output), [output, ansi]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <TerminalIcon className="size-5 text-accent-500" />
        <h2 className="text-h2">Live-терминал</h2>
        <div className="ml-auto flex items-center gap-2">
          <StatusBadge status={status} />
          <button
            type="button"
            onClick={() => setOutput("")}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-bg-subtle border border-border/40 hover:bg-bg-card text-small"
            title="Очистить экран"
          >
            <Trash2 className="size-3.5" />
            Clear
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                wsRef.current?.close();
              } catch {
                /* noop */
              }
              connect();
            }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-bg-subtle border border-border/40 hover:bg-bg-card text-small"
            title="Переподключиться"
          >
            Reconnect
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border/40 bg-black overflow-hidden flex flex-col h-[60vh] min-h-[400px]">
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto p-3 font-mono text-[12px] leading-[1.4] text-emerald-200 whitespace-pre-wrap break-all"
          onClick={() => inputRef.current?.focus()}
          dangerouslySetInnerHTML={{ __html: html || "<span class='text-zinc-500'>Подключение…</span>" }}
        />
        <div className="border-t border-emerald-500/20 bg-black px-2 py-1.5 flex items-center gap-2">
          <span className="text-emerald-400 font-mono text-small select-none">$</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={status !== "open"}
            placeholder={status === "open" ? "введите команду…" : "терминал не подключён"}
            className="flex-1 bg-transparent border-0 outline-none text-emerald-200 font-mono text-small placeholder:text-emerald-700/60 disabled:opacity-50"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={submit}
            disabled={status !== "open"}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40 text-[11px] font-mono"
            title="Enter"
          >
            <CornerDownLeft className="size-3.5" />
          </button>
        </div>
      </div>

      {errMsg && (
        <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-500 text-small">
          {errMsg}
        </div>
      )}

      <div className="text-[11px] text-fg-subtle">
        ⚠ Это <strong>root-shell</strong> на хосте. Все команды выполняются от имени root через
        <code className="font-mono mx-1 px-1 py-0.5 rounded bg-bg-subtle border border-border/40">
          nsenter -t 1
        </code>
        и логируются в audit log.
        <br />
        Хоткеи: <kbd>↑</kbd>/<kbd>↓</kbd> — история, <kbd>Ctrl+C</kbd> — прерывание,{" "}
        <kbd>Ctrl+D</kbd> — EOF, <kbd>Tab</kbd> — автодополнение.
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: "idle" | "connecting" | "open" | "closed" | "error" }) {
  if (status === "open")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 text-[11px] font-mono">
        <Wifi className="size-3" /> CONNECTED
      </span>
    );
  if (status === "connecting")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-500 border border-amber-500/30 text-[11px] font-mono">
        <Wifi className="size-3 animate-pulse" /> CONNECTING…
      </span>
    );
  if (status === "error")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-500/10 text-rose-500 border border-rose-500/30 text-[11px] font-mono">
        <WifiOff className="size-3" /> ERROR
      </span>
    );
  if (status === "closed")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-500/10 text-zinc-500 border border-zinc-500/30 text-[11px] font-mono">
        <WifiOff className="size-3" /> CLOSED
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-zinc-500/10 text-zinc-500 border border-zinc-500/30 text-[11px] font-mono">
      IDLE
    </span>
  );
}

// Helper для cn-фалбэка чтобы tsbuilder не ругался при tree-shaking.
void cn;
